/**
 * The trust part of the console API (design 13, 15): the autonomy screen's overview and cell
 * controls, the shadow-mode report, change-set editing and approval requests, Improve's summary,
 * configuration as code and the data export (notifications are the console API's notification centre).
 * `ConsoleApi` extends it and
 * `ConsoleClient` mixes `trustMethods` in, so other areas can grow their own parts the same way.
 */
import type {
  AutonomyCell, AutonomyOverrideResult, ChangeSetView, ConfigExport, InsightsSummary, ShadowStatsView, TrustOverview,
} from '@takeit4me/shared'
import type { Http } from './http'
import { qs } from './http'

export interface AutonomySettingsBody { veto_minutes?: number; max_level?: number; locked_reason?: string | null; reason: string }
export interface ChangeSetEditBody { title?: string; changes: { entity: string; op: string; key: string; after?: unknown; summary?: string }[] }
export interface ShadowStatsQuery { weeks?: number; from?: string; to?: string; request_type?: string; channel?: string }
export interface ConnectionsView {
  payments: { provider: string; live: boolean; detail: string }
  code: { app_configured: boolean; connections: { id: string; provider: string; account: string; repos: string[]; connected_at: string }[] }
  webhooks: { active: number }
}
export interface RetestResult { runs: { suite: string; passed: number; failed: number }[]; changes: { id: string; subject_key: string; channel: string; change: string; to?: number; reason?: string }[] }

export interface TrustApi {
  /** The company's autonomy rules and this week's error budget, overall and per request type. */
  trustOverview(): Promise<TrustOverview>
  /** Per week, request type and channel: drafts sent unchanged, lightly edited, edited or rewritten, and 0 / 1 / 2+ edits. */
  shadowStats(q?: ShadowStatsQuery): Promise<ShadowStatsView>
  /** Removes a channel's own level so it follows "all channels" again (a raise goes to a second admin). */
  removeAutonomyOverride(id: string, reason: string): Promise<AutonomyOverrideResult & { removed: boolean }>
  /** Veto window, company limit and its reason for one cell. */
  autonomySettings(id: string, body: AutonomySettingsBody): Promise<{ cell: AutonomyCell | null; change_set: ChangeSetView | null }>
  /** Runs the evals behind the autonomy rules now and re-evaluates every cell. */
  retestAutonomy(): Promise<RetestResult>
  /** Edit by hand: replaces a draft's changes; it is validated and tested again. */
  editChangeSet(id: string, body: ChangeSetEditBody): Promise<ChangeSetView>
  /** Asks the other admins and supervisors to approve a tested change set. */
  requestApproval(id: string, note?: string): Promise<ChangeSetView>
  improveSummary(): Promise<InsightsSummary>
  configExport(): Promise<ConfigExport>
  configImport(body: { title?: string; files: Record<string, unknown> }): Promise<ChangeSetView>
  /** The company's data as NDJSON (admins). */
  dataExport(): Promise<Blob>
  /** Payments, code repositories and webhooks: what is connected (Settings, Payments and connections). */
  connections(): Promise<ConnectionsView>
  /** Records a customer's rating given to staff (by phone or email). */
  rateConversation(id: string, body: { rating: number; comment?: string }): Promise<{ ok: true }>
}

const enc = encodeURIComponent

export function trustMethods(http: Http): TrustApi {
  return {
    trustOverview: () => http.get<TrustOverview>('/trust/overview'),
    shadowStats: (q = {}) => http.get<ShadowStatsView>(`/trust/shadow-stats${qs(q as Record<string, string | number | undefined>)}`),
    removeAutonomyOverride: (id, reason) => http.post(`/studio/autonomy/${enc(id)}/remove`, { reason }),
    autonomySettings: (id, body) => http.post(`/studio/autonomy/${enc(id)}/settings`, body),
    retestAutonomy: () => http.post<RetestResult>('/studio/autonomy/retest'),
    editChangeSet: (id, body) => http.patch<ChangeSetView>(`/change-sets/${enc(id)}`, body),
    requestApproval: (id, note) => http.post<ChangeSetView>(`/change-sets/${enc(id)}/request-approval`, note ? { note } : {}),
    improveSummary: () => http.get<InsightsSummary>('/improve/summary'),
    configExport: () => http.get<ConfigExport>('/config/export'),
    configImport: (body) => http.post<ChangeSetView>('/config/import', body),
    dataExport: async () => (await http.raw('GET', '/data/export/stream')).blob(),
    connections: () => http.get<ConnectionsView>('/settings/connections'),
    rateConversation: (id, body) => http.post<{ ok: true }>(`/conversations/${enc(id)}/rating`, body),
  }
}
